import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

import Typography from '~/shared/components/Typography';

type InputSize = 'sm' | 'md' | 'md-responsive' | 'lg';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  leftAdornment?: ReactNode;
  rightAdornment?: ReactNode;
  placeholder?: string;
  error?: string;
  errorId?: string;
  hasError?: boolean;
  inputClassName?: string;
  wrapperClassName?: string;
  leftAdornmentClassName?: string;
  rightAdornmentClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      wrapperClassName,
      inputClassName,
      placeholder,
      leftAdornment,
      rightAdornment,
      leftAdornmentClassName,
      rightAdornmentClassName,
      error,
      errorId,
      hasError,
      ...props
    },
    ref
  ) => {
    const sizeWrapperClasses: Record<InputSize, string> = {
      sm: 'h-9',
      md: 'h-11',
      'md-responsive': 'h-9 2xl:h-11',
      lg: 'h-[52px]',
    };

    const inputPaddingRight = rightAdornment ? 'pr-10' : 'pr-3';
    const inputPaddingLeft = leftAdornment ? 'pl-10' : 'pl-3';

    const inputTextClasses: Record<InputSize, string> = {
      sm: 'text-sm',
      md: 'text-sm',
      'md-responsive': 'text-sm 2xl:text-base',
      lg: 'text-sm',
    };

    const leftAdornmentSizeClasses: Record<InputSize, string> = {
      sm: 'left-3',
      md: 'left-3',
      'md-responsive': 'left-3',
      lg: 'left-3.5',
    };

    const rightAdornmentSizeClasses: Record<InputSize, string> = {
      sm: 'right-3',
      md: 'right-3',
      'md-responsive': 'right-3',
      lg: 'right-3.5',
    };

    return (
      <div className={clsx('flex flex-col', wrapperClassName)}>
        <div className={clsx('relative', sizeWrapperClasses[size])}>
          {leftAdornment && (
            <div
              className={clsx(
                'absolute top-0 bottom-0 left-0 flex items-center pointer-events-none',
                leftAdornmentSizeClasses[size],
                leftAdornmentClassName
              )}
            >
              {leftAdornment}
            </div>
          )}

          {rightAdornment && (
            <div
              className={clsx(
                'absolute top-0 bottom-0 right-0 flex items-center pointer-events-none',
                rightAdornmentSizeClasses[size],
                rightAdornmentClassName
              )}
            >
              {rightAdornment}
            </div>
          )}

          <input
            ref={ref}
            placeholder={placeholder}
            className={clsx(
              'w-full h-full border rounded-lg bg-white shadowsm transition-colors duration-200 ease-in-out',
              'placeholder:text-primary-900 text-gray-800',
              inputPaddingRight,
              inputPaddingLeft,
              inputTextClasses[size],
              error || hasError
                ? 'border-red hover:border-red focus:border-red'
                : 'border-gray-300 hover:border-primary-500 focus:border-primary-300',
              'focus:ring-none focus:outline-none',
              'disabled:text-gray-400 disabled:placeholder:text-gray-700 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-300',
              props.readOnly ? 'bg-gray-50 text-gray-500 cursor-default' : '',
              inputClassName
            )}
            {...props}
          />
        </div>
        {error && (
          <Typography id={errorId} variant="buttons-2" className="text-red text-[10px] mt-1 ">
            {error}
          </Typography>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
export { Input };
